import calculate_percentile from "./_calculatedPercentile";
import { getNorm } from '../utils/normHelpers';
import { calculateAge } from "../utils/ageCalculator";

function createChart(
  groupData,
  showGroup,
  normsObject,
  showNorms,
  athleteData,
  showAthlete,
  currentCampus,
  currentProgram,
  currentGender,
  athleteNamesList,
  testToPlot,
  testTitleString,
  onMarkerDoubleClick,
  lastClickRef,
  plotRebuild,
  plot_percentile
) {

  // Normalize currentGender to an array
  const genders = currentGender === "All" ? ["Male", "Female"] : [currentGender];

  const colors = [
    "#FFA500", "#0000FF", "#FF0000", "#008000", "#800080",
    "#FFFF00", "#00FFFF", "#FF00FF", "#00FF00", "#0F00FF",
  ];

  const keys = [
    "Student_8_12", "Rebuild_8_12", "Student_12_14", "Rebuild_12_14",
    "Student_14_18", "Rebuild_14_18", "Forever", "Rebuild_Forever", "Next_Level", "Prebuild"
  ];

  let categories = [];
  let series = [];
  let normSeries = [];
  let validChart = false;
  let colorsToPlot = [];

  // Handle athlete data
  const athleteAvailable = athleteData && Object.keys(athleteData).length > 0;
  let athleteSeries = [];
  let athletePlotColor = "#000000";
  let athleteGender = "";
  let athleteKey = "";

  if (athleteAvailable) {
    ({ athleteKey, athleteGender } = getAthleteKeyAndGender(athleteData, plotRebuild));
  }

  if (groupData && Object.keys(groupData).length > 0) {
    keys.forEach((key, keyIndex) => {

      if (!shouldIncludeKey(key, currentProgram, plotRebuild)) return;

      // Map grouping key -> program + ageGroup for norm lookup
      const mapKey = (k) => {
        if (k.startsWith('Student_')) {
          return { programKey: 'Student', ageGroup: k.replace('Student_', '') };
        }
        if (k.startsWith('Rebuild_')) {
          const agePart = k.replace('Rebuild_', '');
          return { programKey: 'Student', ageGroup: agePart === 'Forever' ? '19+' : agePart };
        }
        if (k === 'Next_Level') return { programKey: 'Next Level', ageGroup: 'Any' };
        if (k === 'Forever') return { programKey: 'Forever', ageGroup: '19+' };
        return { programKey: k, ageGroup: 'Any' };
      };

      const { programKey, ageGroup } = mapKey(key);

      genders.forEach((gender) => {
        const category = `${key} (${gender[0]})`;
        const plotPos = categories.length;
        categories.push(category);
        colorsToPlot.push(colors[keyIndex]);

        const dataSource = groupData[key]?.[gender];
        if (!dataSource || dataSource.length === 0) return;

        const norm = getNorm(testToPlot, programKey, ageGroup, gender, normsObject);

        const { seriesData, athleteDataPoint, foundAthlete } = processDataPoints(
          dataSource,
          testToPlot,
          norm,
          plot_percentile,
          plotPos,
          athleteNamesList,
          currentCampus,
          athleteAvailable,
          athleteData,
          athleteKey,
          testTitleString
        );

        if (seriesData.length > 0) validChart = true;

        series.push({
          name: `${key}${gender[0]}`,
          data: seriesData,
        });

        if (norm.mean != null && !plot_percentile && showNorms) {
          normSeries.push(createNormSeries(norm, plotPos, key));
        }

        if (athleteAvailable && athleteDataPoint && foundAthlete) {
          athleteSeries = [{
            name: "Athlete",
            data: [athleteDataPoint],
          }];
          athletePlotColor = colors[keyIndex];
        }
      });
    });
  }

  if (!validChart) {
    return {
      series: [],
      options: {},
      type: "scatter",
    };
  }

  const chartOptions = prepareChartOptions(
    series,
    normSeries,
    athleteSeries,
    categories,
    colorsToPlot,
    testTitleString,
    showGroup,
    showAthlete,
    genders,
    athleteGender,
    athleteAvailable,
    athletePlotColor,
    lastClickRef,
    onMarkerDoubleClick,
    plot_percentile
  );

  return chartOptions;
}

function getAthleteKeyAndGender(athleteData, plotRebuild) {
  let athleteKey = athleteData.athleteProfileForTest.program.replace(/ ATHlete/g, "");
  const athleteAge = athleteData.athleteProfileForTest.age;
  const athleteGender = athleteData.athleteProfileForTest.gender;

  if (athleteKey.startsWith("Rebuild") && !plotRebuild) {
    return {};
  }

  if (athleteKey.startsWith("Next")) {
    athleteKey = "Next_Level";
  } else if (athleteKey.startsWith("Forever")) {
    athleteKey = "Forever";
  } else if (athleteKey.startsWith("Student")) {
    athleteKey = getStudentKey(athleteAge);
  } else if (athleteKey.startsWith("Rebuild")) {
    athleteKey = getRebuildKey(athleteAge);
  } else if (athleteKey.startsWith("Prebuild")) {
    athleteKey = "Prebuild";
  }

  return { athleteKey, athleteGender };
}

function getStudentKey(age) {
  //if (age >= 19) return "Forever";
  if (age < 12) return "Student_8_12";
  if (age >= 14) return "Student_14_18";
  return "Student_12_14";
}

function getRebuildKey(age) {
  //if (age >= 19) return "Rebuild_Forever";
  if (age < 12) return "Rebuild_8_12";
  if (age >= 14) return "Rebuild_14_18";
  return "Rebuild_12_14";
}

function shouldIncludeKey(key, currentProgram, plotRebuild) {
  const isRebuildKey = key.startsWith("Rebuild");
  const programMatch = key.startsWith(currentProgram.replace(/ /g, "_"));
  const rebuildMatch = isRebuildKey && plotRebuild;

  if (currentProgram === "All") {
    return !isRebuildKey || plotRebuild;
  }
  return programMatch || rebuildMatch;
}

function processDataPoints(
  dataSource,
  testToPlot,
  norm,
  plot_percentile,
  plotPos,
  athleteNamesList,
  currentCampus,
  athleteAvailable,
  athleteData,
  athleteKey,
  testTitleString
) {
  const seriesData = [];
  let athleteDataPoint = null;
  let foundAthlete = false;

  // Sort data by age
  dataSource.sort((a, b) => {
    const ageA = new Date(a.measurementDate) - new Date(a.athleteProfileForTest.dob);
    const ageB = new Date(b.measurementDate) - new Date(b.athleteProfileForTest.dob);
    return ageA - ageB;
  });

  dataSource.forEach((dataPoint, index) => {
    let {
      campus,
      athleteProfileForTest: { name, campus:  athleteCampus},
      measurementDate,
    } = dataPoint;

    //console.log(index.toString(), '.1. ', athleteKey, name, campus, athleteCampus, currentCampus, campus?.startsWith(currentCampus));

    if (!campus)
      campus = athleteCampus;
    
    //console.log(index.toString(), '.2. ', athleteKey, name, campus, athleteCampus, currentCampus, campus?.startsWith(currentCampus));

    if (campus && currentCampus !== "All" && !campus.startsWith(currentCampus)) return;
    if (!athleteNamesList.includes(name)) return;

    console.log(index.toString(), '.3. ', athleteKey, name, campus, athleteCampus, currentCampus, campus?.startsWith(currentCampus));
    
    const testData = dataPoint[testToPlot];
    if (!testData?.isValid || typeof testData.value !== "number" || testData.value === -99999.0)
      return;

    const plotX = plotPos + (index / dataSource.length) - 0.5;
    const { valueToPlot, tooltipString } = getValueAndTooltip(
      testData.value,
      norm,
      plot_percentile,
      testTitleString,
      dataPoint,
      measurementDate
    );

    seriesData.push({
      x: plotX, //0*plotX - 1,
      y: valueToPlot,
      customTooltip: tooltipString,
    });
    
    if (
      athleteAvailable &&
      name === athleteData.athleteProfileForTest.name &&
      measurementDate === athleteData.measurementDate &&
      athleteKey.startsWith(dataPoint.athleteProfileForTest.program.replace(/ ATHlete/g, ""))
    ) {
      // eslint-disable-next-line no-debugger
      debugger;
      athleteDataPoint = {
        x: plotX,
        y: valueToPlot,
        customTooltip: tooltipString,
      };
      foundAthlete = true;
    }
  });

  return { seriesData, athleteDataPoint, foundAthlete };
}

function getValueAndTooltip(value, norm, plot_percentile, testTitleString, dataPoint, measurementDate) {
  let percentileValue = norm.mean!==null ? calculate_percentile(value, norm.mean, norm.sd):null;

  if (testTitleString.includes("[s]") || testTitleString.includes("[sec]")) {
    percentileValue = 100.0 - percentileValue;
  }

  const valueToPlot = plot_percentile ? percentileValue : value;
  const tooltipString = createTooltipString(
    value,
    percentileValue,
    plot_percentile,
    norm.mean != null,
    dataPoint,
    measurementDate
  );

  return { valueToPlot, tooltipString };
}

function createTooltipString(
  value,
  percentileValue,
  plot_percentile,
  normsAvailable,
  dataPoint,
  measurementDate
) {
  const name = dataPoint.athleteProfileForTest.name;
  const age = calculateAge(new Date(dataPoint.athleteProfileForTest.dob), new Date(measurementDate), 1);
  const [date, time] = measurementDate.split("T");
  const measureTime = time.split(".")[0];

  const valueInfo = plot_percentile
    ? normsAvailable
      ? `% (${value.toFixed(2)})`
      : ""
    : normsAvailable && typeof percentileValue === "number"
    ? ` (${percentileValue.toFixed(1)}%)`
    : "";

  return `${valueInfo} for\n ${name} at\n ${measureTime} on\n ${date} [${age.toFixed(1)} years old]`;
}

function createNormSeries(norm, plotPos, key) {
  return {
    name: `${key} norms`,
    type: "line",
    data: [
      {
        x: plotPos,
        y: norm.mean - 3 * norm.sd,
        customTooltip: "Normative mean - 3SD",
      },
      {
        x: plotPos,
        y: norm.mean + 3 * norm.sd,
        customTooltip: "Normative mean + 3SD",
      },
    ],
  };
}

function prepareChartOptions(
  series,
  normSeries,
  athleteSeries,
  categories,
  colorsToPlot,
  testTitleString,
  showGroup,
  showAthlete,
  genders,
  athleteGender,
  athleteAvailable,
  athletePlotColor,
  lastClickRef,
  onMarkerDoubleClick,
  plot_percentile
) {
  // Append normSeries and athleteSeries to series
  series = series.concat(normSeries);
  if (showAthlete && athleteAvailable) {
    series = series.concat(athleteSeries);
  }
  colorsToPlot = colorsToPlot.concat(
    normSeries.map(() => "#FFFFFF"),
    athleteAvailable && showAthlete ? [athletePlotColor] : []
  );

  const seriesCount = series.length;
  let markerSize = 8;
  let strokeWidth = 1.5;
  let xFontSize = 16;

  if (seriesCount >= 3 && seriesCount <= 6) {
    xFontSize = 14;
    markerSize -= 1;
    strokeWidth = 1.25;
  } else if (seriesCount >= 7 && seriesCount <= 10) {
    xFontSize = 14;
    markerSize -= 2;
    strokeWidth = 1.0;
  } else if (seriesCount >= 11 && seriesCount <= 14) {
    xFontSize = 12;
    markerSize -= 3;
    strokeWidth = 0.75;
  } else if (seriesCount >= 15) {
    xFontSize = 9;
    markerSize -= 4;
    strokeWidth = 0.5;
  }

  const markers = generateMarkers(
    series,
    showGroup,
    showAthlete,
    genders,
    athleteGender,
    athleteAvailable,
    markerSize,
    strokeWidth
  );

  const options = {
    colors: colorsToPlot,
    title: {
      text: testTitleString,
      align: "center",
      style: {
        fontSize: 32,
        fontWeight: "bold",
        color: "black",
      },
    },
    chart: {
      type: "scatter",
      height: 350,
      zoom: {
        enabled: false,
      },
      animations: {
        enabled: false,
        animateGradually: {
          enabled: false,
        },
        dynamicAnimation: {
          enabled: false,
        },
      },
      events: {
        markerClick: function (event, chartContext, { seriesIndex, dataPointIndex }) {
          const marker = chartContext.w.config.series[seriesIndex].data[dataPointIndex];
          const customTooltip = marker.customTooltip;

          if (lastClickRef.current && new Date().getTime() - lastClickRef.current < 300) {
            lastClickRef.current = null;
            onMarkerDoubleClick(customTooltip);
          } else {
            lastClickRef.current = new Date().getTime();
          }
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    markers,
    stroke: {
      show: true,
    },
    legend: {
      show: false,
    },
    yaxis: {
      min: testTitleString.includes("PHV") ? -10.0 : 0.0,
      max: plot_percentile ? 100 : undefined,
      labels: {
        style: {
          fontSize: 16,
          color: "white",
        },
        formatter: function (value) {
          return value === Math.round(value) ? value.toFixed(0) : value.toFixed(3);
        },
      },
      title: {
        text: plot_percentile ? "Percentile [%]" : "Absolute value",
        offsetX: -10,
        style: {
          fontSize: 20,
          fontWeight: "bold",
          color: "black",
        },
      },
    },
    xaxis: {
      tickAmount: categories.length + 1,
      min: -1.0,
      max: categories.length,
      labels: {
        show: true,
        formatter: function (value) {
          if (typeof value == "number" && Math.round(value) === value) {
            let key = categories[Math.round(value)];
            if (!key) return "";

            key = key
              .replace(/_/g, " ")
              .replace("Student ", "")
              .replace("Forever", "FE")
              .replace("Prebuild", "PB")
              .replace("Next Level", "NL")
              .replace("Rebuild ", "R.");

            return key;
          }
          return "";
        },
        style: {
          fontSize: xFontSize,
          fontWeight: "bold",
          color: "black",
        },
      },
      title: {
        text: "Programs by gender",
        style: {
          fontSize: 20,
          fontWeight: "bold",
          color: "black",
        },
      },
    },
    tooltip: {
      theme: "dark",
      custom: function (opts) {
        const dataPoint = opts.ctx.w.config.series[opts.seriesIndex].data[opts.dataPointIndex];
        const value = opts.series[opts.seriesIndex][opts.dataPointIndex];
        return value.toFixed(2) + dataPoint.customTooltip;
      },
      style: {
        colors: ["black"],
      },
    },
  };

  return {
    type: "scatter",
    series,
    options,
  };
}

function generateMarkers(
  series,
  showGroup,
  showAthlete,
  genders,
  athleteGender,
  athleteAvailable,
  markerSize,
  strokeWidth
) {
  const markers = {
    shape: [],
    size: [],
    strokeColors: [],
    strokeWidth: [],
    fillOpacity: [],
    hover: {
      size: 2 * markerSize,
    },
  };

  const groupStrokeWidth = showGroup ? strokeWidth : 0;
  const groupMarkerSize = showGroup ? markerSize : 0;

  series.forEach((s, index) => {
    let fillOpacity = 1;
    let markerShape = "circle";
    let strokeColor = "#000000";
    let size = groupMarkerSize;
    let strokeW = groupStrokeWidth;

    if (s.name === "Athlete") {
      fillOpacity = 1;
      size = showAthlete ? 2 * markerSize : 0;
      strokeW = 2.0;
      strokeColor = athleteGender === "Male" ? "#000000" : "#FFFFFF";
    } else {
      if (athleteAvailable && showAthlete && !s.name.includes("norms")) {
        fillOpacity = 0.33;
      }
      const genderIndex = index % genders.length;
      const gender = genders[genderIndex];
      strokeColor = gender === "Male" || s.name.includes("norms") ? "#000000" : "#FFFFFF";
    }

    markers.shape.push(markerShape);
    markers.size.push(size);
    markers.strokeColors.push(strokeColor);
    markers.strokeWidth.push(strokeW);
    markers.fillOpacity.push(fillOpacity);
  });

  return markers;
}

export default createChart;